"use client";

import React, { useEffect } from "react";
import {
  OakFlex,
  OakInfo,
  OakRadioGroup,
  OakSmallSecondaryToggleButton,
  OakSpan,
  OakUL,
} from "@oaknational/oak-components";

import { useGoogleClassroomAddonStore } from "@/store";
import { LessonListItem } from "@/components/LessonListItem";
import type { LessonListItem as LessonItem } from "@/types/ui";

type Props = {
  lessons?: LessonItem[];
  phase: string;
  subject: string;
  unitTitle: string;
  getPupilLessonUrl: (
    programmeSlug: string,
    unitSlug: string,
    lessonSlug: string,
  ) => string;
  onLessonSelected?: (lessonSlug: string) => void;
  onLessonPreviewed?: (lessonSlug: string) => void;
};
export const LessonList = ({
  lessons,
  unitTitle,
  getPupilLessonUrl,
  onLessonSelected,
  onLessonPreviewed,
}: Props) => {
  const setLessons = useGoogleClassroomAddonStore((state) => state.setLessons);
  const setLessonSelected = useGoogleClassroomAddonStore(
    (state) => state.setLessonSelected,
  );
  const unsetLessons = useGoogleClassroomAddonStore(
    (state) => state.unsetLessons,
  );
  const isGradeSynced = useGoogleClassroomAddonStore(
    (state) => state.isGradeSynced,
  );
  const toggleGradeSync = useGoogleClassroomAddonStore(
    (state) => state.toggleGradeSync,
  );

  useEffect(() => {
    if (!lessons) return;

    const parsedLessons = lessons.map((lesson) => ({
      title: lesson.lessonData.title,
      unitSlug: lesson.unitSlug,
      lessonSlug: lesson.lessonSlug,
      programmeSlug: lesson.programmeSlug,
      maxPoints: lesson.exitQuizCount,
      unitTitle,
    }));

    setLessons(parsedLessons);
    return () => {
      unsetLessons();
    };
  }, [lessons, setLessons, unsetLessons, unitTitle]);

  return (
    <OakFlex
      $gap="spacing-16"
      $alignItems="stretch"
      $background={"bg-decorative4-very-subdued"}
      $flexDirection="column"
      $pa="spacing-16"
      $borderRadius="border-radius-m"
    >
      <OakFlex
        $gap="spacing-8"
        $alignItems="center"
        $width="100%"
        $justifyContent="space-between"
      >
        <OakSpan>Select a lesson</OakSpan>
        <OakFlex $alignItems="center" $gap="spacing-4">
          <OakInfo
            buttonProps={{ type: "button" }}
            id={"gradebook-toggle-tooltip"}
            tooltipPosition="top-right"
            hint={"If this is disabled, you won’t be able to mark pupil work."}
          />
          <OakSmallSecondaryToggleButton
            toggleOn={isGradeSynced}
            onClick={() => {
              toggleGradeSync(!isGradeSynced);
            }}
            aria-describedby={"gradebook-toggle-tooltip"}
            type="button"
          >
            {`Marking ${isGradeSynced ? "enabled" : "disabled"}`}
          </OakSmallSecondaryToggleButton>
        </OakFlex>
      </OakFlex>
      <OakRadioGroup
        name={"lesson-selection"}
        onChange={(e) => {
          const slug = (e.target as HTMLInputElement).value;
          setLessonSelected();
          onLessonSelected?.(slug);
        }}
      >
        <OakUL
          $display="flex"
          $flexDirection="column"
          $gap="spacing-12"
          aria-label="A list of lessons"
          $reset
          $width="100%"
        >
          {lessons?.map((lesson, i) => (
            <LessonListItem
              key={lesson.lessonSlug}
              index={i + 1}
              lesson={lesson}
              getPupilLessonUrl={getPupilLessonUrl}
              onLessonPreviewed={onLessonPreviewed}
            />
          ))}
        </OakUL>
      </OakRadioGroup>
    </OakFlex>
  );
};
